import { useMemo, useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { Signup } from '@/api/signups';
import { parseDateString } from '@/utils/dates';
import { sortByString, sortByDate } from '@/utils/table-sort';

import { Table } from '@/components/ui/table';

type SignupsTableProps = {
  signups: Signup[];
  onRowClick: (signup: Signup) => void;
};

export function SignupsTable({ signups, onRowClick }: SignupsTableProps) {
  const { t } = useTranslation();

  const formatDate = useCallback((dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
      const parsedDate = parseDateString(dateString);
      return parsedDate ? parsedDate.toLocaleDateString() : 'Invalid Date';
    } catch {
      return 'Invalid Date';
    }
  }, []);

  const signupsTableHeaders = useMemo(
    () => [
      {
        key: 'created_at',
        label: t('admin-users-signups-created-at'),
        format: (value: unknown) => formatDate(value as string),
        sortFn: sortByDate('created_at'),
      },
      {
        key: 'user_email',
        label: t('admin-users-signups-email'),
        sortFn: sortByString('user_email'),
      },
      {
        key: 'name',
        label: t('admin-users-signups-name'),
        sortFn: sortByString('name'),
      },
      {
        key: 'requestedRole',
        label: t('admin-users-signups-requested-role'),
        sortFn: sortByString('requestedRole'),
      },
      {
        key: 'clinic',
        label: t('admin-users-signups-clinic'),
        sortFn: sortByString('clinic'),
      },
    ],
    [t, formatDate]
  );

  return <Table headers={signupsTableHeaders} data={signups} onRowClick={onRowClick} />;
}
